import React, { useContext, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import AccountContext from 'contexts/AccountContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import GqlClientContext from 'contexts/GqlClientContext';
import FlexCentered from 'components/Common/FlexCentered';
import Typography from 'kingpin/atoms/Typography';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import { DRIVER_APP_EMBED_ORIGIN, EmbedFrame } from '../BonusAppEmbed';
import getEmbedProgramsByConfigId from '../BonusAppEmbed/getEmbedProgramsByConfigId';

// ---------------------------------------------------------------------------
// Phone geometry
// ---------------------------------------------------------------------------

const SCREEN_W = 375;
const SCREEN_H = 762;
const BEZEL = 11; // px — thin bezel matching modern iPhones

const SHELL_W = SCREEN_W + BEZEL * 2; // 397
const SHELL_H = SCREEN_H + BEZEL * 2; // 784

const TARGET_H = 668; // desired visual height inside overlay
const SCALE = TARGET_H / SHELL_H; // ~0.852

// Layout footprint of the scaled phone (what the overlay flex sees)
const FOOTPRINT_W = Math.round(SHELL_W * SCALE); // ~338
const FOOTPRINT_H = TARGET_H;

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 600;
  padding: 24px;
  overflow-y: auto;
`;

// ---------------------------------------------------------------------------
// Phone frame
// ---------------------------------------------------------------------------

// Wraps the phone with the correct layout footprint so flex centering works
const PhoneWrap = styled.div`
  position: relative;
  width: ${FOOTPRINT_W}px;
  height: ${FOOTPRINT_H}px;
  flex-shrink: 0;
`;

// Absolutely centered + scaled so visual matches the PhoneWrap footprint
const PhoneShell = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${SCALE});
  transform-origin: center center;
  width: ${SHELL_W}px;
  height: ${SHELL_H}px;
  background: #1c1c1e;
  border-radius: 52px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.14),
    0 0 0 1.5px rgba(0, 0, 0, 0.9),
    0 32px 80px rgba(0, 0, 0, 0.9),
    0 8px 24px rgba(0, 0, 0, 0.5);
`;

// The glass screen
const Screen = styled.div`
  position: absolute;
  top: ${BEZEL}px;
  left: ${BEZEL}px;
  right: ${BEZEL}px;
  bottom: ${BEZEL}px;
  border-radius: 42px;
  background: #000;
  overflow: hidden;
`;

// Home indicator — white pill at bottom of screen
const HomeIndicator = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 134px;
  height: 5px;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 100px;
  z-index: 2;
  pointer-events: none;
`;

// Side buttons — protruding from the shell edges
const SideBtn = styled.div<{
  $side: 'left' | 'right';
  $top: number;
  $h: number;
}>`
  position: absolute;
  ${(p) => p.$side}: -4px;
  top: ${(p) => p.$top}px;
  width: 4px;
  height: ${(p) => p.$h}px;
  background: #2c2c2e;
  border-radius: ${(p) => (p.$side === 'left' ? '3px 0 0 3px' : '0 3px 3px 0')};
`;

// ---------------------------------------------------------------------------
// Modal component
// ---------------------------------------------------------------------------

const ViewDriverAppModal = ({
  isOpen,
  close,
  origin = DRIVER_APP_EMBED_ORIGIN,
  carrierSlug,
  programSlug: programSlugOverride,
}: {
  isOpen: boolean;
  close: () => void;
  // Which embed to load — live by default; the Live/Preview menu passes the
  // preview origin here.
  origin?: string;
  // Board-level carrier-slug override. When omitted the account-level Advanced
  // settings are used, so existing callers are unaffected.
  carrierSlug?: string;
  // Overrides the account's default program slug for this open — e.g. the
  // combined tab's home entry passes HOME_PROGRAM_SLUG so the embed lands on
  // the home screen. Defaults to the account setting (the per-program flow).
  programSlug?: string;
}) => {
  const { id, driverAppEmbedCarrierSlug, driverAppEmbedProgramSlug } =
    useContext(AccountContext);
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { client } = useContext(GqlClientContext);
  const { bonusPrograms, bonusPeriodsLookup } = useContext(
    DatasetDefinitionsContext,
  );

  // Every program on the account (name + non-archived periods), keyed by config
  // id, so the embed handshakes can resolve any program (PERIODS for one,
  // PROGRAMS for the home cards).
  const programsByConfigId = useMemo(
    () => getEmbedProgramsByConfigId({ bonusPrograms, bonusPeriodsLookup }),
    [bonusPrograms, bonusPeriodsLookup],
  );

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const effectiveCarrierSlug = carrierSlug ?? driverAppEmbedCarrierSlug;
  const programSlug =
    programSlugOverride ?? driverAppEmbedProgramSlug ?? 'bonus';

  return createPortal(
    <Overlay onClick={close}>
      <PhoneWrap onClick={(e) => e.stopPropagation()}>
        <PhoneShell>
          {/* Left: mute toggle */}
          <SideBtn $side="left" $top={158} $h={32} />
          {/* Left: volume up */}
          <SideBtn $side="left" $top={210} $h={60} />
          {/* Left: volume down */}
          <SideBtn $side="left" $top={284} $h={60} />
          {/* Right: power */}
          <SideBtn $side="right" $top={220} $h={86} />

          <Screen>
            {!effectiveCarrierSlug || !bonusOverview ? (
              <FlexCentered>
                <Typography.Body type="Body 13">
                  {!effectiveCarrierSlug
                    ? 'Driver app embed not configured.'
                    : 'Bonus information not found.'}
                </Typography.Body>
              </FlexCentered>
            ) : (
              <EmbedFrame
                accountId={id}
                carrierSlug={effectiveCarrierSlug}
                programSlug={programSlug}
                bonusDocument={bonusOverview.actualRewardOutput}
                periodStatus={bonusOverview.bonusPeriod.status}
                gqlClient={client}
                gatewayApi={api}
                programsByConfigId={programsByConfigId}
                origin={origin}
              />
            )}
            <HomeIndicator />
          </Screen>
        </PhoneShell>
      </PhoneWrap>
    </Overlay>,
    document.body,
  );
};

export default ViewDriverAppModal;
